import { useMemo, memo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useDateRange } from '../../contexts/DateRangeContext';
import { useOutageHistory, useCommunityHistory } from '../../hooks/useHistoricalData';
import { LoadingSkeleton } from '../../components/atoms/LoadingSkeleton/LoadingSkeleton';
import { Card } from '../../components/atoms/Card/Card';
import { FullscreenChart, useIsFullscreen } from '../../components/molecules/FullscreenChart/FullscreenChart';
import { CHART_CONFIG, COMMUNITY_COLORS, shouldAnimate } from '../../constants';
import type { CommunityTimeSeries } from '../../../types/outage';
import './CommunityOutagesChart.css';

// Cache for formatted time strings to avoid repeated expensive toLocaleString calls
const timeFormatCache = new Map<string, string>();

// Helper to format timestamps for chart display with caching
function formatChartTime(ts: string, todayDateStr: string): string {
  const cacheKey = `${ts}-${todayDateStr}`;
  const cached = timeFormatCache.get(cacheKey);
  if (cached) return cached;

  const date = new Date(ts);
  const isToday = date.toLocaleDateString("en-US", { timeZone: "America/Halifax" }) === todayDateStr;

  let result: string;
  if (isToday) {
    result = date.toLocaleTimeString("en-US", {
      timeZone: "America/Halifax",
      hour: "numeric",
      minute: "2-digit",
    });
  } else {
    result = date.toLocaleString("en-US", {
      timeZone: "America/Halifax",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }

  // Limit cache size to prevent memory leaks
  if (timeFormatCache.size > 5000) {
    timeFormatCache.clear();
  }
  timeFormatCache.set(cacheKey, result);
  return result;
}

// Inner component that consumes fullscreen context - memoized to prevent unnecessary re-renders
interface CommunityChartContentProps {
  chartData: Array<Record<string, string | number>>;
  allCommunities: string[];
  legendPayload: Array<{ value: string; type: 'square'; color: string }>;
  animationsEnabled: boolean;
}

const CommunityChartContent = memo(function CommunityChartContent({
  chartData,
  allCommunities,
  legendPayload,
  animationsEnabled,
}: CommunityChartContentProps) {
  const isFullscreen = useIsFullscreen();

  return (
    <ResponsiveContainer width="100%" height={isFullscreen ? "100%" : 350}>
      <AreaChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke={CHART_CONFIG.gridStroke} />
        <XAxis
          dataKey="time"
          stroke={CHART_CONFIG.axisStroke}
          tick={{ fill: CHART_CONFIG.axisStroke }}
        />
        <YAxis
          stroke={CHART_CONFIG.axisStroke}
          tick={{ fill: CHART_CONFIG.axisStroke}}
          tickCount={10}
        />
        <Tooltip
          contentStyle={CHART_CONFIG.tooltipStyle}
          content={({ active, payload, label }) => {
            if (!active || !payload || payload.length === 0) return null;

            // Filter to non-zero, sort by value descending, take top 15
            const nonZero = payload
              .filter((entry) => (entry.value as number) > 0)
              .sort((a, b) => (b.value as number) - (a.value as number));

            const top15 = nonZero.slice(0, 15);
            const remaining = nonZero.slice(15);
            const remainingTotal = remaining.reduce((sum: number, e) => sum + (e.value as number), 0);

            return (
              <div style={{ ...CHART_CONFIG.tooltipStyle, padding: '10px' }}>
                <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>{label}</div>
                {top15.map((entry) => (
                  <div key={entry.name} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                    <span style={{ color: entry.color }}>{entry.name}</span>
                    <span>{Math.round(entry.value as number)} customers</span>
                  </div>
                ))}
                {remaining.length > 0 && (
                  <div style={{ marginTop: '8px', fontStyle: 'italic', opacity: 0.8 }}>
                    ...and {remaining.length} more ({Math.round(remainingTotal).toLocaleString()} customers)
                  </div>
                )}
              </div>
            );
          }}
        />
        <Legend
          wrapperStyle={{ color: CHART_CONFIG.axisStroke }}
          // Recharts types incorrectly omit payload from Legend props
          {...{ payload: legendPayload }}
        />
        {/* Render most impacted first so larger areas form the base of the stack */}
        {allCommunities.map((community, i) => {
          const color = COMMUNITY_COLORS[i % COMMUNITY_COLORS.length];
          return (
            <Area
              key={community}
              type="monotone"
              dataKey={community}
              stackId="outages"
              stroke={color}
              fill={color}
              name={community}
              legendType="none"
              isAnimationActive={animationsEnabled}
              animationDuration={CHART_CONFIG.animation.duration}
            />
          );
        })}
      </AreaChart>
    </ResponsiveContainer>
  );
});

export function CommunityOutagesChart() {
  const { startDate, endDate } = useDateRange();
  const { data: outageResponse, isLoading: outageLoading } = useOutageHistory(startDate, endDate);
  const { data: communityResponse, isLoading: communityLoading } = useCommunityHistory(startDate, endDate);

  // Defensive: handle both old (raw array) and new ({ data, meta }) API formats
  const outageHistory = Array.isArray(outageResponse)
    ? outageResponse
    : (outageResponse?.data ?? []);
  const communityHistory = Array.isArray(communityResponse)
    ? communityResponse
    : (communityResponse?.data ?? []);

  const { allCommunities, chartData, legendPayload } = useMemo(() => {
    // Pre-compute today's date string once for all formatting calls
    const todayDateStr = new Date().toLocaleDateString("en-US", { timeZone: "America/Halifax" });

    // Calculate total outage count per community across all timestamps
    const communityTotals = new Map<string, number>();
    communityHistory.forEach((d: CommunityTimeSeries) => {
      const current = communityTotals.get(d.community) || 0;
      communityTotals.set(d.community, current + d.outageCount);
    });

    // Sort communities by total descending
    const sortedCommunities = Array.from(communityTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([community]) => community);

    const topCommunities = sortedCommunities.slice(0, 20);
    const otherCommunities = sortedCommunities.slice(20);
    const hasOther = otherCommunities.length > 0;

    // Build Map for O(1) community data lookup by timestamp - O(n) instead of O(n²)
    // Key: timestamp, Value: Map<community, outageCount>
    const communityByTimestamp = new Map<string, Map<string, number>>();
    communityHistory.forEach((d: CommunityTimeSeries) => {
      let timestampMap = communityByTimestamp.get(d.timestamp);
      if (!timestampMap) {
        timestampMap = new Map<string, number>();
        communityByTimestamp.set(d.timestamp, timestampMap);
      }
      timestampMap.set(d.community, d.outageCount);
    });

    // Use outageHistory timestamps to include ALL time points (even when 0 outages)
    const allTimestamps = Array.from(
      new Set(outageHistory.map((d: { timestamp: string }) => d.timestamp)),
    ).sort();

    // Create chart data with O(1) lookups instead of O(n) filter per timestamp
    const chartData = allTimestamps.map((timestamp) => {
      const dataPoint: Record<string, string | number> = {
        timestamp,
        time: formatChartTime(timestamp, todayDateStr),
      };

      // Initialize ALL communities to 0
      sortedCommunities.forEach((community) => {
        dataPoint[community] = 0;
      });

      // Fill in actual values using O(1) Map lookup instead of O(n) filter
      const timestampData = communityByTimestamp.get(timestamp);
      if (timestampData) {
        timestampData.forEach((outageCount, community) => {
          dataPoint[community] = outageCount;
        });
      }

      return dataPoint;
    });

    // Build custom legend payload: top 20 + "Other" entry
    // Cast color to string to allow the gray "Other" entry
    const legendPayload: Array<{ value: string; type: 'square'; color: string }> = topCommunities.map((community, i) => ({
      value: community,
      type: 'square' as const,
      color: COMMUNITY_COLORS[i % COMMUNITY_COLORS.length] as string,
    }));

    if (hasOther) {
      legendPayload.push({
        value: `Other (${otherCommunities.length} communities)`,
        type: 'square' as const,
        color: '#888888',
      });
    }

    return { allCommunities: sortedCommunities, chartData, legendPayload };
  }, [outageHistory, communityHistory]);

  // Determine if animations should be enabled based on data size
  // For community chart, factor in number of communities since each adds an Area
  const animationsEnabled = shouldAnimate(chartData.length * Math.min(allCommunities.length, 20));

  return (
    <Card className="chart-container">
      <FullscreenChart title="Customer Outages by Community">
        <h3>Outages by Community Over Time</h3>
        {outageLoading || communityLoading ? (
          <LoadingSkeleton variant="chart" />
        ) : chartData.length > 0 && allCommunities.length > 0 ? (
          <CommunityChartContent
            chartData={chartData}
            allCommunities={allCommunities}
            legendPayload={legendPayload}
            animationsEnabled={animationsEnabled}
          />
        ) : (
          <p className="no-data">No community outage data for selected range</p>
        )}
      </FullscreenChart>
    </Card>
  );
}
